import React from 'react';
import { BORDER_RADIUS_TINY, FONTS, TYPOGRAPHY } from '@/styles';
import { StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { Theme, useTheme } from '@/contexts/theme-context';

export enum Level {
  INFO,
  NEGATIVE,
  POSITIVE,
  WARNING,
}

export const getAlertStyle = (level: Level, theme: Theme) => {
  return {
    [Level.INFO]: {
      backgroundColor: theme.sk.color.bg.blue,
      color: theme.sk.color.text.blue,
    },
    [Level.NEGATIVE]: {
      backgroundColor: theme.sk.color.bg.error,
      color: theme.sk.color.text.error,
    },
    [Level.POSITIVE]: {
      backgroundColor: theme.sk.color.bg.success,
      color: theme.sk.color.text.success,
    },
    [Level.WARNING]: {
      backgroundColor: theme.sk.color.bg.warning,
      color: theme.sk.color.text.warning,
    },
  }[level];
};

interface BaseProps {
  children: React.ReactNode;
  level: Level;
  style?: StyleProp<ViewStyle>;
}

type Props =
  | BaseProps
  | (BaseProps & {
      Icon: React.ComponentType<SvgProps>;
      iconProps?: SvgProps;
    });

export const Alert: React.FC<Props> = ({ children, level, style, ...rest }) => {
  const theme = useTheme();
  const { backgroundColor, color } = getAlertStyle(level, theme);
  return (
    <View
      style={[
        styles.base,
        {
          backgroundColor,
        },
        style,
      ]}
    >
      {'Icon' in rest ? (
        <View style={styles.iconContainer}>
          <rest.Icon {...rest.iconProps} />
        </View>
      ) : null}
      {typeof children === 'string' ? (
        <Text
          style={[
            TYPOGRAPHY.h4,
            styles.text,
            {
              color,
            },
          ]}
        >
          {children}
        </Text>
      ) : (
        children
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    borderRadius: BORDER_RADIUS_TINY,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  iconContainer: {
    marginRight: 8,
  },
  text: {
    ...FONTS.primarySemiBold,
  },
});
